import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import PermissionsSelector from '@/pages/procedures/create/_partials/PermissionsSelector';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useTranslation } from 'react-i18next';

export default function SheetDuplicateProcedureTemplate({ open, onClose, procedureId, procedureName, onSuccess }) {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [permissions, setPermissions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Réinitialiser le formulaire à l'ouverture
    useEffect(() => {
        if (open) {
            setName(procedureName ? `${procedureName} (copie)` : '');
            setPermissions([]);
        }
    }, [open, procedureName]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            toast.error(t('procedures:veuillezSaisirUnNomPourLaProcedure'));
            return;
        }

        setIsSubmitting(true);
        try {
            await horodatyApi.procedureTemplates.duplicate(procedureId, {
                name: name.trim(),
                selected_companies: permissions,
            });
            toast.success(t('procedures:procedureDupliqueeAvecSucces'));
            onSuccess?.();
            onClose();
        } catch (err) {
            console.error('Erreur duplication:', err);
            toast.error(err?.response?.data?.message || t('procedures:erreurLorsDeLaDuplication'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(isOpen) => !isOpen && !isSubmitting && onClose()}>
            <SheetContent className="w-[90vw] md:w-[500px]! md:max-w-[500px]! flex flex-col p-0">
                <SheetHeader className="px-6 py-4 border-b border-border">
                    <SheetTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                        <Copy className="h-5 w-5" />
                        {t('procedures:dupliquerLaProcedure')}
                    </SheetTitle>
                </SheetHeader>

                <form id="form-duplicate-procedure" onSubmit={handleSubmit} className="flex-1 overflow-y-auto scrollbar-hide px-6 py-5 space-y-6">
                    {/* Nom du template */}
                    <Field>
                        <FieldLabel>{t('procedures:nomDeLaNouvelleProcedure')}</FieldLabel>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('procedures:exInspectionBatimentCopie')}
                            required
                        />
                    </Field>

                    {/* Sélection des permissions */}
                    <Field>
                        <FieldLabel>{t('procedures:accesCollaborateurs')}</FieldLabel>
                        <p className="text-[12px] text-muted-foreground mb-3">
                            {t('procedures:selectionnezLesEntreprisesEtCollaborateursQuiAur')}
                        </p>
                        <PermissionsSelector
                            selected_companies={permissions}
                            onChange={setPermissions}
                        />
                    </Field>
                </form>

                <SheetFooter className="px-6 py-4 border-t border-border flex gap-3">
                    <Button
                        type="button"
                        variant="soft"
                        color="neutral"
                        onClick={onClose}
                        className="flex-1"
                        disabled={isSubmitting}
                    >
                        {t('procedures:annuler')}
                    </Button>
                    <SubmitButton
                        form="form-duplicate-procedure"
                        className="flex-1 px-4 py-3 rounded-lg"
                        isSubmitting={isSubmitting}
                        loadingText={t('procedures:duplication')}
                    >
                        {t('procedures:dupliquer')}
                    </SubmitButton>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
